import { Add, DeleteOutline, ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { type KeyboardEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import { getSelectedCampaigns } from '/@/store/carbon-calculator/selectors';
import { useCampaignCrops } from '../hooks';
import { type CampaignCrop, MAX_CROPS_PER_CAMPAIGN, type SelectedCampaigns } from '../utils';
import { SelectField } from './field-form';
import { inputSx, sectionSx } from './styles';

interface CampaignsCardProps {
    /** Index of the open campaign, or -1 when all are closed. */
    selectedSeason: number;
    onSelectSeason: (index: number) => void;
    onNextSeason: () => void;
    onCalculate: (campaigns: SelectedCampaigns) => void;
    onCropSelected: (crop: string) => void;
    onYieldEntered: () => void;
}

/**
 * The store keeps the yield converted to t per hectare, so the field keeps what the user
 * typed and only resets when the store clears the yield (a repeated crop was cleared).
 */
function YieldField({
    crop,
    onChange,
    onKeyDown,
}: {
    crop: CampaignCrop;
    onChange: (value: string) => void;
    onKeyDown: (event: KeyboardEvent) => void;
}) {
    const units = useUnitProvider();
    const toDisplay = (value: number | string) =>
        value === '' ? '' : String(units.toWeightPerArea(Number(value) * 1000, 0).value);
    const [text, setText] = useState(() => toDisplay(crop.yield));

    // Only an external clear (a repeated crop was cleared) resets what was typed.
    useEffect(() => {
        if (crop.yield === '') setText('');
    }, [crop.yield]);

    return (
        <TextField
            type='number'
            size='small'
            fullWidth
            label={_('Rendimiento')}
            value={text}
            sx={inputSx}
            inputProps={{ min: 0 }}
            InputProps={{
                endAdornment: <InputAdornment position='end'>{units.getUnit('weight/area', undefined)}</InputAdornment>,
            }}
            onChange={(event) => {
                setText(event.target.value);
                onChange(event.target.value);
            }}
            onKeyDown={onKeyDown}
        />
    );
}

export default function CampaignsCard({
    selectedSeason,
    onSelectSeason,
    onNextSeason,
    onCalculate,
    onCropSelected,
    onYieldEntered,
}: CampaignsCardProps) {
    const units = useUnitProvider();
    const selectedCampaigns: SelectedCampaigns = useSelector(getSelectedCampaigns);
    const { changeCrop, changeYield, addCropToCampaign, removeCropFromCampaign } = useCampaignCrops({
        onCalculate,
        onCropSelected,
        onYieldEntered,
    });

    const seasons = Object.entries(selectedCampaigns);

    return (
        <Stack spacing={2} sx={sectionSx}>
            <Stack spacing={0.5}>
                <Typography variant='h6' color='primary'>
                    {_('Campañas')}
                </Typography>
                <Typography variant='body2' color='primary.mui.inactive'>
                    {_(
                        'Cargá los cultivos de cada campaña y sus rendimientos. Podés cargar hasta 3 cultivos por campaña.'
                    )}
                </Typography>
            </Stack>
            <Stack>
                {seasons.map(([season, campaign], seasonIndex) => {
                    // A lone crop can't be removed, so its row has no delete column.
                    const canRemove = campaign.crops.length > 1;
                    const cropNames = campaign.crops
                        .filter((crop) => crop.crop && crop.yield)
                        .map((crop) => crop.crop)
                        .join(', ');

                    return (
                        <Accordion
                            key={season}
                            disableGutters
                            expanded={selectedSeason === seasonIndex}
                            onChange={(_event, expanded) => onSelectSeason(expanded ? seasonIndex : -1)}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                // Let the summary shrink so a long crop list ends in an ellipsis
                                // instead of pushing the row past the card on a phone.
                                sx={{ '& .MuiAccordionSummary-content': { minWidth: 0 } }}
                            >
                                <Stack direction='row' spacing={2} alignItems='baseline' minWidth={0}>
                                    <Typography variant='subtitle1' color='primary' fontWeight={500} flexShrink={0}>
                                        {`${season}/${Number(season) + 1}`}
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        color={cropNames ? 'primary.mui.inactive' : 'warning.main'}
                                        noWrap
                                        minWidth={0}
                                        title={cropNames || undefined}
                                    >
                                        {cropNames || _('Sin cultivos cargados')}
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    {campaign.crops.map((crop, cropIndex) => (
                                        // Crops have no id. Keying on the count too remounts the rows when one is
                                        // removed, so each yield field starts from its own crop's value.
                                        // On a phone the crop takes the whole row, with the yield and delete below.
                                        <Box
                                            // biome-ignore lint/suspicious/noArrayIndexKey: see above
                                            key={`${cropIndex}-${campaign.crops.length}`}
                                            display='grid'
                                            gridTemplateColumns={
                                                canRemove
                                                    ? { xs: '1fr auto', sm: '1fr 1fr auto' }
                                                    : { xs: '1fr', sm: '1fr 1fr' }
                                            }
                                            gap={1}
                                            alignItems='center'
                                        >
                                            <Box gridColumn={{ xs: '1 / -1', sm: 'auto' }}>
                                                <SelectField
                                                    label={_('Cultivo')}
                                                    value={crop.key || undefined}
                                                    options={crop.options}
                                                    onChange={(value) => {
                                                        const option = crop.options.find((o) => o.value === value);
                                                        if (option) changeCrop(season, cropIndex, option);
                                                    }}
                                                />
                                            </Box>
                                            <YieldField
                                                crop={crop}
                                                onChange={(value) =>
                                                    changeYield(
                                                        season,
                                                        cropIndex,
                                                        value === '' ? '' : units.fromWeightPerArea(value) / 1000
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' && seasonIndex + 1 !== seasons.length) {
                                                        onNextSeason();
                                                    }
                                                }}
                                            />
                                            {canRemove && (
                                                <IconButton
                                                    aria-label={_('Eliminar')}
                                                    onClick={() => removeCropFromCampaign(season, cropIndex)}
                                                >
                                                    <DeleteOutline />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    <Button
                                        color='secondary'
                                        size='small'
                                        startIcon={<Add />}
                                        disabled={campaign.crops.length >= MAX_CROPS_PER_CAMPAIGN}
                                        onClick={() => addCropToCampaign(season)}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        {_('Agregar cultivo')}
                                    </Button>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
        </Stack>
    );
}
